import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { Approval } from '../../types';
import { getStatusText } from '../../../utils/component/state-filter-combobox/state-cb.component';
import { WidgetMessageService } from '../../services/widget-message.service';
import { InvalidateLoadedQueries } from '../../state/entity.state';

export interface HasApprovalState {
  id: string
  approval?: Approval
  status?: string | Approval
  [key: string]: unknown
}

export interface EntityUpdateService<T> {
  update(entity: T): Observable<unknown>
}

@Component({
  selector: 'pixl-state-badge',
  standalone: true,
  imports: [NgClass, MatMenuModule, MatButtonModule, TranslocoPipe],
  templateUrl: './state-badge.component.html',
  styleUrl: './state-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StateBadgeComponent {
  public entity = input.required<HasApprovalState>();
  public entityService = input.required<EntityUpdateService<unknown>>();
  public refresh$ = input.required<BehaviorSubject<void>>();
  public readonly badgeClicked = output<string>();

  public entityClass = input<new () => HasApprovalState>(
    class implements HasApprovalState {
      id = '';
      approval: Approval = 'Dormant';
      [key: string]: unknown;
    }
  );

  public currentStatus = computed<Approval>(() => {
    const ent = this.entity();
    return ((ent.approval ?? ent.status) ?? 'Dormant') as Approval;
  });

  private readonly store = inject(Store);
  private readonly widgetService = inject(WidgetMessageService);
  private readonly translocoService = inject(TranslocoService);

  public readonly availableStatuses: Approval[] = ['Dormant', 'Edit', 'Pending', 'Live'];

  public getTranslationKey(approval: Approval): string {
    return getStatusText(approval);
  }

  public getStatusColor(approval: Approval): string {
    return {
      Dormant: 'badge-dormant',
      Edit: 'badge-edit',
      Pending: 'badge-pending',
      Live: 'badge-live',
    }[approval] || '';
  }

  public async changeState(newStatus: Approval): Promise<void> {
    const currentEntity = this.entity();
    if (this.currentStatus() === newStatus) return;

    const statusUpdates: Record<string, unknown> = {};
    if (currentEntity.approval !== undefined) {
      statusUpdates.approval = newStatus;
    }
    if (currentEntity.status !== undefined) {
      statusUpdates.status = newStatus;
    }
    if (currentEntity.approval === undefined && currentEntity.status === undefined) {
      statusUpdates.approval = newStatus;
    }

    const updatedEntity = Object.assign(new (this.entityClass())(), currentEntity, statusUpdates);

    try {
      await firstValueFrom(this.entityService().update(updatedEntity));
      this.store.dispatch(new InvalidateLoadedQueries());
      this.refresh$().next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('state_saved'),
        messageTye: 'mtSuccess'
      });
    }
    catch (error) {
      console.error('Fehler beim Ändern des Status:', error);
      this.widgetService.setMessage({
        message: this.translocoService.translate('preview.saveError'),
        messageTye: 'mtError'
      });
    }
  }
}
